import { FaServer, FaCloud, FaShieldAlt, FaRocket } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { aboutData, statistics } from '../../data'
import { FadeIn, StaggerContainer, StaggerItem } from '../animations/ScrollAnimations'
import FloatingParticles from '../animations/FloatingParticles'

export default function About(): JSX.Element {
  return (
    <div className="section bg-portfolio-black relative overflow-hidden">
      {/* Floating Particles Background */}
      <FloatingParticles />
      
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-portfolio-green/20 to-transparent"></div>
        <div className="absolute top-20 left-20 w-72 h-72 bg-portfolio-green/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-portfolio-green/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <FadeIn direction="up" delay={0.2}>
              <div>
                <motion.h2 
                  className="heading-2"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  {aboutData.title} <span className="gradient-text">{aboutData.subtitle}</span>
                </motion.h2>
                <motion.p 
                  className="text-large mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.01 }}
                >
                  {aboutData.description}
                </motion.p>
              </div>
            </FadeIn>

            <StaggerContainer>
              <div className="space-y-6">
                {aboutData.paragraphs.map((paragraph, index) => (
                  <StaggerItem key={index}>
                    <motion.p 
                      className="text-medium cursor-pointer"
                      whileHover={{ x: 10, scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      {paragraph}
                    </motion.p>
                  </StaggerItem>
                ))}
              </div>
            </StaggerContainer>

            <motion.div 
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <motion.a 
                href={aboutData.primaryButton.href} 
                className="btn-primary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {aboutData.primaryButton.text}
              </motion.a>
              <motion.a 
                href={aboutData.secondaryButton.href} 
                className="btn-secondary"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                {aboutData.secondaryButton.text}
              </motion.a>
            </motion.div>
          </div>

          {/* Right Content - Key Achievements */}
          <StaggerContainer>
            <div className="space-y-6">
              {aboutData.achievements.map((achievement, index) => {
                let IconComponent = FaServer
                if (achievement.icon === 'FaCloud') IconComponent = FaCloud
                if (achievement.icon === 'FaShieldAlt') IconComponent = FaShieldAlt
                if (achievement.icon === 'FaRocket') IconComponent = FaRocket

                return (
                  <StaggerItem key={index}>
                    <motion.div 
                      className="card cursor-pointer"
                      whileHover={{ 
                        scale: 1.05, 
                        y: -8,
                        boxShadow: "0 20px 40px rgba(149, 255, 49, 0.2)"
                      }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <motion.div 
                          className="w-12 h-12 bg-green-400/20 rounded-lg flex items-center justify-center"
                          whileHover={{ rotate: 360, scale: 1.2 }}
                          transition={{ duration: 0.6 }}
                        >
                          <IconComponent className="text-3xl text-green-400" />
                        </motion.div>
                        <div>
                          <motion.h3 
                            className="text-xl font-semibold text-white"
                            whileHover={{ color: "#95FF31" }}
                          >
                            {achievement.title}
                          </motion.h3>
                          <motion.p 
                            className="text-gray-400"
                            whileHover={{ x: 5 }}
                          >
                            {achievement.description}
                          </motion.p>
                        </div>
                      </div>
                    </motion.div>
                  </StaggerItem>
                )
              })}
            </div>
          </StaggerContainer>
        </div>

        {/* Stats Section */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: statistics.about.technologies, label: "Technologies" },
              { value: statistics.about.projects, label: "Projects" },
              { value: statistics.about.satisfaction, label: "Client Satisfaction" }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="card text-center cursor-pointer"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -5,
                  boxShadow: "0 20px 40px rgba(149, 255, 49, 0.15)"
                }}
              >
                <motion.div 
                  className="text-3xl font-bold text-green-400 mb-2"
                  whileHover={{ scale: 1.1 }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  )
} 